import { useCallback, useEffect, useState } from "react";
import { api } from "../lib/api";
import { isIOS, isStandalone } from "./registerSW";

function urlBase64ToUint8Array(base64) {
  const padding = "=".repeat((4 - (base64.length % 4)) % 4);
  const raw = window.atob((base64 + padding).replace(/-/g, "+").replace(/_/g, "/"));
  return Uint8Array.from([...raw].map((c) => c.charCodeAt(0)));
}

export const pushSupported = () => "serviceWorker" in navigator && "PushManager" in window && "Notification" in window;
/** iOS only delivers Web Push to apps installed on the home screen (iOS 16.4+). */
export const pushBlockedOnIOS = () => isIOS() && !isStandalone();

export function usePush() {
  const [status, setStatus] = useState(null);
  const [permission, setPermission] = useState(typeof Notification !== "undefined" ? Notification.permission : "unsupported");
  const [subscribed, setSubscribed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const refresh = useCallback(async () => {
    try { const { data } = await api.get("/push/status"); setStatus(data); } catch { setStatus(null); }
    if (pushSupported()) {
      try { const reg = await navigator.serviceWorker.ready; setSubscribed(!!(await reg.pushManager.getSubscription())); } catch { setSubscribed(false); }
    }
  }, []);
  useEffect(() => { refresh(); }, [refresh]);

  const enable = async () => {
    setBusy(true); setError("");
    try {
      if (!pushSupported()) throw new Error("Deze browser ondersteunt geen meldingen.");
      if (pushBlockedOnIOS()) throw new Error("Zet Playmaker eerst op je beginscherm (Deel → Zet op beginscherm). Daarna kun je meldingen aanzetten.");
      const perm = await Notification.requestPermission();
      setPermission(perm);
      if (perm !== "granted") throw new Error("Meldingen zijn geweigerd. Zet ze aan via de browserinstellingen.");
      const { data } = await api.get("/push/vapid-public-key");
      const reg = await navigator.serviceWorker.ready;
      const sub = (await reg.pushManager.getSubscription()) || (await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: urlBase64ToUint8Array(data.public_key) }));
      await api.post("/push/subscribe", { subscription: sub.toJSON(), ua: navigator.userAgent, platform: isIOS() ? "ios" : /android/i.test(navigator.userAgent) ? "android" : "desktop" });
      setSubscribed(true);
      await refresh();
    } catch (e) { setError(e?.response?.data?.detail || e.message || "Meldingen aanzetten mislukt."); }
    finally { setBusy(false); }
  };

  const disable = async () => {
    setBusy(true); setError("");
    try {
      const reg = await navigator.serviceWorker.ready;
      const sub = await reg.pushManager.getSubscription();
      if (sub) { await api.delete("/push/subscribe", { data: { endpoint: sub.endpoint } }); await sub.unsubscribe(); }
      setSubscribed(false);
      await refresh();
    } catch (e) { setError(e.message || "Uitzetten mislukt."); }
    finally { setBusy(false); }
  };

  const setPref = async (cat, value) => {
    const { data } = await api.put("/push/prefs", { [cat]: value });
    setStatus((s) => ({ ...s, prefs: data.prefs }));
  };

  const sendTest = async () => {
    setBusy(true); setError("");
    try { const { data } = await api.post("/push/test"); if (!data.ok) setError("Geen melding afgeleverd — controleer of meldingen op dit apparaat aanstaan."); return data; }
    catch (e) { setError(e?.response?.data?.detail || "Testmelding mislukt."); }
    finally { setBusy(false); }
  };

  return { status, permission, subscribed, busy, error, enable, disable, setPref, sendTest, refresh };
}
